import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { personalProject, pictureModels, type PictureModel } from '@shared/services/pictureService'
import { WwLoaderFill } from '@shared/components/WwLoader'
import { CompareSlider } from '@shared/components/CompareSlider'
import { blobOf, fileOf, type Rect, type TextLine } from '../canvas/pictureOps'
import { applyTextOn, findSunburst, readTextOn, textEstimate } from '../canvas/textEngine'
import '../components/pictureSources.css'
import './editTextPage.css'

/**
 * Edit Text, as a page (Huanrui, 2026-10-07)
 * -------------------------------------------------------------
 * Laid out from his sketch. Down the left: the two ways in (this computer,
 * the clipboard), Whole or Area, Detect, the lines -- what is printed on the
 * left, what it should say on the right -- and at the foot a large Apply with
 * Download and Copy as picture beside it. On the right, one large stage for the
 * picture, with a download in its corner. Once Apply has worked, the stage is
 * the Upscale page's line to drag across -- the original to its left, the
 * edited picture to its right -- and the box is put away (Huanrui,
 * 2026-10-07); pressing Area brings the picture back to draw a new one.
 *
 * ONE PICTURE AT A TIME, AND NOTHING KEPT. Unlike the generate and upscale
 * pages this one has no history: the result lives in this tab until it is
 * downloaded or copied. Until then, leaving the page or bringing in
 * another picture asks first (in WonderWorks, our own box; closing the tab,
 * the browser's, whose words a site cannot set).
 *
 * The work itself is the canvas card's (textEngine.ts): Gemini reads the
 * lines, Sunburst redraws the changed ones, and only those are let through
 * over the original. With Area, everything changed goes as one part. No
 * Fine-tune here (Huanrui, 2026-10-07).
 */

interface Picture {
  canvas: HTMLCanvasElement
  url: string
  title: string
  png: boolean
}

interface Result {
  canvas: HTMLCanvasElement
  url: string
  file: File
  credits: number
  said: string
}

export function EditTextPage() {
  const navigate = useNavigate()
  const [project, setProject] = useState('')
  const [models, setModels] = useState<PictureModel[]>([])
  const [picture, setPicture] = useState<Picture | null>(null)
  const [mode, setMode] = useState<'whole' | 'area'>('whole')
  const [area, setArea] = useState<Rect | null>(null)
  const [lines, setLines] = useState<TextLine[]>([])
  const [detecting, setDetecting] = useState(false)
  const [applying, setApplying] = useState(false)
  const [result, setResult] = useState<Result | null>(null)
  /** The result has been downloaded or copied, so leaving loses nothing. */
  const [kept, setKept] = useState(false)
  const [copying, setCopying] = useState(false)
  /** The stage: the picture to draw a box on, or the line between original and edited. */
  const [view, setView] = useState<'pick' | 'compare'>('pick')
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState('')
  const [note, setNote] = useState('')
  const [ask, setAsk] = useState<{ title: string; text: string; yes: string; then: () => void } | null>(null)
  const upload = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    let cancelled = false
    void Promise.all([personalProject(), pictureModels()])
      .then(([id, found]) => {
        if (cancelled) return
        setProject(id)
        setModels(found)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'This page could not load')
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Object URLs let go of when they are replaced.
  useEffect(() => () => (picture ? URL.revokeObjectURL(picture.url) : undefined), [picture])
  useEffect(() => () => (result ? URL.revokeObjectURL(result.url) : undefined), [result])

  /* ---------- nothing kept: asking before it is lost ---------- */

  const unsaved = Boolean(result) && !kept
  const unsavedRef = useRef(unsaved)
  unsavedRef.current = unsaved

  // Closing or reloading the tab: the browser's own box.
  useEffect(() => {
    if (!unsaved) return
    const stop = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', stop)
    return () => window.removeEventListener('beforeunload', stop)
  }, [unsaved])

  // A link elsewhere in WonderWorks: our own box first.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!unsavedRef.current || event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const link = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!link || link.target === '_blank') return
      const to = new URL(link.href, window.location.href)
      if (to.origin !== window.location.origin || to.pathname === window.location.pathname) return
      event.preventDefault()
      event.stopPropagation()
      setAsk({
        title: 'Leave Edit Text?',
        text: 'This page keeps nothing. The picture and its changed text will be lost unless you download it or copy it first.',
        yes: 'Leave',
        then: () => {
          unsavedRef.current = false
          navigate(`${to.pathname}${to.search}${to.hash}`)
        },
      })
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [navigate])

  /* ---------- a picture in ---------- */

  const take = useCallback(async (file: File) => {
    setError('')
    setNote('')
    try {
      const bitmap = await createImageBitmap(file)
      const canvas = document.createElement('canvas')
      canvas.width = bitmap.width
      canvas.height = bitmap.height
      canvas.getContext('2d')?.drawImage(bitmap, 0, 0)
      bitmap.close()
      setPicture({
        canvas,
        url: URL.createObjectURL(file),
        title: (file.name || 'picture').replace(/\.[a-z0-9]+$/i, ''),
        png: file.type === 'image/png',
      })
      setArea(null)
      setLines([])
      setResult(null)
      setKept(false)
      setView('pick')
    } catch {
      setError('That picture could not be opened')
    }
  }, [])

  /** Another picture in: asked about first when the result would be lost. */
  const bringIn = useCallback(
    (files: File[]) => {
      const file = files.find((one) => one.type.startsWith('image/'))
      if (!file) return
      if (unsavedRef.current) {
        setAsk({
          title: 'Replace this picture?',
          text: 'The edited picture has not been downloaded or copied. Bringing in another one loses it.',
          yes: 'Replace',
          then: () => void take(file),
        })
        return
      }
      void take(file)
    },
    [take],
  )

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return
      const files = [...(event.clipboardData?.files ?? [])].filter((file) => file.type.startsWith('image/'))
      if (files.length === 0) return
      event.preventDefault()
      bringIn(files)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [bringIn])

  const fromClipboard = useCallback(async () => {
    try {
      const items = await navigator.clipboard.read()
      for (const item of items) {
        const type = item.types.find((one) => one.startsWith('image/'))
        if (!type) continue
        const blob = await item.getType(type)
        bringIn([new File([blob], `pasted.${type.split('/')[1] || 'png'}`, { type })])
        return
      }
      setError('There is no picture on the clipboard')
    } catch {
      setError('The browser would not hand over the clipboard — press Ctrl+V instead')
    }
  }, [bringIn])

  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    setDragging(false)
    bringIn([...event.dataTransfer.files])
  }

  /* ---------- reading and changing ---------- */

  const sunburst = useMemo(() => findSunburst(models), [models])
  const changes = lines.filter((line) => line.to.trim() !== line.text.trim()).length
  const estimate = picture
    ? textEstimate(lines, { width: picture.canvas.width, height: picture.canvas.height }, mode === 'area', sunburst)
    : { parts: 0, credits: 0 }

  const detect = async () => {
    if (!picture || !project) return
    if (mode === 'area' && !area) return
    setDetecting(true)
    setError('')
    setNote('')
    try {
      const found = await readTextOn(project, picture.canvas, mode === 'area' ? area : null)
      if (found.lines.length === 0) setError(mode === 'area' ? 'No text could be read in the box' : 'No text could be read on this picture')
      setLines(found.lines)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The text could not be read')
    } finally {
      setDetecting(false)
    }
  }

  const apply = async () => {
    if (!picture || !project || changes === 0) return
    setApplying(true)
    setError('')
    setNote('')
    try {
      const done = await applyTextOn({
        projectId: project,
        picture: picture.canvas,
        png: picture.png,
        title: `${picture.title} text`,
        lines,
        together: mode === 'area',
      })
      const file = await fileOf(done.canvas, `${picture.title} text`, picture.png)
      setResult({ canvas: done.canvas, url: URL.createObjectURL(file), file, credits: done.credits, said: done.said })
      setKept(false)
      setView('compare')
      setNote(
        done.missed > 0
          ? `${done.made} of ${done.made + done.missed} parts redrawn · ${done.credits} credits`
          : `Done · ${done.credits} credits`,
      )
      if (done.missed > 0) setError(done.error)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'It could not be done')
    } finally {
      setApplying(false)
    }
  }

  const download = () => {
    if (!result) return
    const link = document.createElement('a')
    link.href = result.url
    link.download = result.file.name
    document.body.appendChild(link)
    link.click()
    link.remove()
    setKept(true)
  }

  /**
   * Copy as picture (Huanrui, 2026-10-07, in place of Send to Miro): the
   * edited picture onto the system clipboard, as a PNG -- the one picture
   * type every browser will put there -- ready to paste into Miro, a deck or
   * a board.
   */
  const copyPicture = async () => {
    if (!result || copying) return
    setCopying(true)
    setError('')
    try {
      const png = result.file.type === 'image/png' ? result.file : await blobOf(result.canvas, 'image/png')
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })])
      setKept(true)
      setNote('Copied — paste it wherever it is wanted.')
    } catch {
      setError('The browser would not put the picture on the clipboard — use Download instead')
    } finally {
      setCopying(false)
    }
  }

  /* ---------- the stage ---------- */

  const frame = useRef<HTMLSpanElement | null>(null)
  const from = useRef<{ x: number; y: number } | null>(null)
  const [draft, setDraft] = useState<Rect | null>(null)
  const at = (event: React.PointerEvent) => {
    const r = frame.current!.getBoundingClientRect()
    return {
      x: Math.min(1, Math.max(0, (event.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (event.clientY - r.top) / r.height)),
    }
  }
  const boxOf = (a: { x: number; y: number }, b: { x: number; y: number }): Rect => ({
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(a.x - b.x),
    h: Math.abs(a.y - b.y),
  })
  const shownBox = draft ?? (mode === 'area' ? area : null)
  const comparing = Boolean(result) && view === 'compare'
  const busy = detecting || applying

  return (
    <div
      className={`etx${dragging ? ' etx--over' : ''}`}
      onDragOver={(event) => {
        event.preventDefault()
        setDragging(true)
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as globalThis.Node | null)) setDragging(false)
      }}
      onDrop={onDrop}
    >
      <section className="etx__side">
        <div className="psrc">
          <button type="button" className="psrc__tile" onClick={() => upload.current?.click()} disabled={busy}>
            <span className="psrc__plus" aria-hidden="true">+</span>
            <strong>This computer</strong>
            <em>or drop a picture</em>
          </button>
          <button type="button" className="psrc__tile" onClick={() => void fromClipboard()} disabled={busy}>
            <span className="psrc__plus" aria-hidden="true">+</span>
            <strong>Clipboard</strong>
            <em>or press Ctrl+V</em>
          </button>
        </div>
        <input
          ref={upload}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(event) => {
            const files = [...(event.target.files ?? [])]
            event.target.value = ''
            bringIn(files)
          }}
        />

        <div className="etx__seg" role="radiogroup" aria-label="What to read">
          <button
            type="button"
            className={mode === 'whole' ? 'on' : ''}
            onClick={() => {
              setMode('whole')
              if (result) setView('compare')
            }}
            aria-pressed={mode === 'whole'}
          >
            Whole picture
          </button>
          <button
            type="button"
            className={mode === 'area' ? 'on' : ''}
            // Back to the picture, to draw a box; the result waits in the line view.
            onClick={() => {
              setMode('area')
              setView('pick')
            }}
            aria-pressed={mode === 'area'}
          >
            Area
          </button>
        </div>

        <button
          type="button"
          className="etx__detect"
          onClick={() => void detect()}
          disabled={!picture || !project || busy || (mode === 'area' && !area)}
        >
          {detecting ? 'Detecting…' : lines.length > 0 ? 'Detect again' : 'Detect text'}
        </button>

        <div className="etx__lines">
          {lines.length > 0 ? (
            <>
              <div className="etx__heads" aria-hidden="true">
                <span>On the picture</span>
                <span>Should say</span>
              </div>
              <div className="etx__list">
                {lines.map((line, index) => {
                  const differs = line.to.trim() !== line.text.trim()
                  const set = (to: string) => setLines((prev) => prev.map((one, at) => (at === index ? { ...one, to } : one)))
                  return (
                    <div key={index} className={`etx__line${differs ? ' etx__line--on' : ''}`}>
                      <span className="etx__from" title={line.text}>
                        {line.text}
                      </span>
                      <span className="etx__arrow" aria-hidden="true">
                        →
                      </span>
                      <span className="etx__to-wrap">
                        <input
                          className="etx__to"
                          value={line.to}
                          placeholder="(take it away)"
                          disabled={applying}
                          onChange={(event) => set(event.target.value)}
                          aria-label={`New text for ${line.text}`}
                        />
                        {differs ? (
                          <button type="button" className="etx__undo" onClick={() => set(line.text)} aria-label="Back to what is printed">
                            ×
                          </button>
                        ) : null}
                      </span>
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            <p className="etx__empty">
              {!picture
                ? 'Bring in a picture to begin.'
                : mode === 'area' && !area
                  ? 'Drag a box on the picture round the text to read, then Detect.'
                  : 'Press Detect to read the text.'}
            </p>
          )}
        </div>

        {error ? <div className="ww-error">{error}</div> : null}
        {note ? <p className="etx__note">{note}</p> : null}

        <div className="etx__foot">
          <button type="button" className="etx__apply" onClick={() => void apply()} disabled={busy || changes === 0 || !project}>
            {applying ? (
              'Applying…'
            ) : (
              <>
                Apply
                <small>
                  {changes > 0
                    ? `${changes} changed${estimate.credits ? ` · about ${estimate.credits} credits` : ''}${estimate.parts > 1 ? ` in ${estimate.parts} parts` : ''}`
                    : 'change a line first'}
                </small>
              </>
            )}
          </button>
          <div className="etx__stack">
            <button type="button" className="etx__act" onClick={download} disabled={!result || busy}>
              Download
            </button>
            <button type="button" className="etx__act" onClick={() => void copyPicture()} disabled={!result || busy || copying}>
              {copying ? 'Copying…' : 'Copy as picture'}
            </button>
          </div>
        </div>
      </section>

      <section className="etx__stage">
        {picture && result && comparing ? (
          <div
            className="etx__compare"
            style={{ ['--r' as string]: String(picture.canvas.width / picture.canvas.height) }}
          >
            <CompareSlider
              before={picture.url}
              after={result.url}
              ratio={picture.canvas.width / picture.canvas.height}
              afterLabel="Edited"
            />
            {applying ? (
              <span className="etx__working">
                <WwLoaderFill label="Editing" size="large" />
              </span>
            ) : null}
          </div>
        ) : picture ? (
          <span
            ref={frame}
            className={`etx__frame${mode === 'area' ? ' etx__frame--area' : ''}`}
            onPointerDown={(event) => {
              if (event.button !== 0 || busy || mode !== 'area') return
              event.preventDefault()
              event.currentTarget.setPointerCapture(event.pointerId)
              from.current = at(event)
              setDraft({ ...from.current, w: 0, h: 0 })
            }}
            onPointerMove={(event) => {
              if (from.current) setDraft(boxOf(from.current, at(event)))
            }}
            onPointerUp={(event) => {
              if (!from.current) return
              const box = boxOf(from.current, at(event))
              from.current = null
              setDraft(null)
              if (box.w > 0.01 && box.h > 0.01) setArea(box)
            }}
          >
            <img src={picture.url} alt={picture.title} draggable={false} />
            {shownBox && shownBox.w > 0 && shownBox.h > 0 ? (
              <span
                className="etx__box"
                style={{
                  left: `${shownBox.x * 100}%`,
                  top: `${shownBox.y * 100}%`,
                  width: `${shownBox.w * 100}%`,
                  height: `${shownBox.h * 100}%`,
                }}
              />
            ) : null}
            {applying ? (
              <span className="etx__working">
                <WwLoaderFill label="Editing" size="large" />
              </span>
            ) : null}
          </span>
        ) : (
          <div className="etx__blank">
            <strong>Edit Text</strong>
            <span>Bring in a picture from this computer or the clipboard, or drop one here.</span>
          </div>
        )}
        {result ? (
          <button type="button" className="etx__dl" onClick={download} aria-label="Download the edited picture" disabled={busy}>
            <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
              <path d="M8 2.6v7.2m0 0 2.8-2.8M8 9.8 5.2 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3.2 12.6h9.6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        ) : null}
      </section>

      {ask ? (
        <div className="etx__ask" role="dialog" aria-modal="true" aria-label={ask.title}>
          <div className="etx__ask-card">
            <strong>{ask.title}</strong>
            <p>{ask.text}</p>
            <div className="etx__ask-btns">
              <button type="button" className="ww-btn ww-btn--ghost" onClick={() => setAsk(null)}>
                Stay
              </button>
              <button
                type="button"
                className="ww-btn ww-btn--primary"
                onClick={() => {
                  const then = ask.then
                  setAsk(null)
                  then()
                }}
              >
                {ask.yes}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
